import React, { useState, useEffect } from "react";
import {
	Elements,
	CardNumberElement,
	CardExpiryElement,
	CardCvcElement,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Separator } from "../fields";

// Stripe element styling
const ELEMENT_OPTIONS = {
	style: {
		base: {
			fontSize: "16px",
			color: "#495057",
			fontFamily:
				'apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif',
			"::placeholder": {
				color: "#6c757d",
			},
		},
		invalid: {
			color: "#dc3545",
			iconColor: "#dc3545",
		},
	},
};

const CreditDebitForm = ({
	setValue,
	watch,
	register,
	unregister,
	config,
	id,
	errors,
}) => {
	const stripe = useStripe();
	const elements = useElements();

	const [cardState, setCardState] = useState({
		number: { complete: false, error: null },
		expiry: { complete: false, error: null },
		cvc: { complete: false, error: null },
	});

	const [isProcessing, setIsProcessing] = useState(false);

	// Register form fields once
	useEffect(() => {
		if (register) {
			register("payment_method_id");
			register("card_details");
			register(`${id}_card_valid`, {
				required: "Please enter valid card details",
			});
		}

		// Cleanup function to unregister fields on unmount
		return () => {
			if (unregister) {
				unregister("payment_method_id");
				unregister("card_details");
				unregister(`${id}_card_valid`);
			}
		};
	}, [register, unregister, id]);

	// Handle card input changes
	const handleCardChange = (field) => (event) => {
		const { error, complete } = event;

		// When card number changes, clear cached PaymentMethod so a fresh one is created.
		// This allows retries with a different card after a decline (e.g. insufficient funds).
		if (field === "number") {
			setValue("payment_method_id", "");
			setValue("card_details", {});
			setValue(`${id}_card_valid`, false);
		}

		setCardState((prev) => ({
			...prev,
			[field]: { complete, error },
		}));
	};

	// Check if all fields are valid
	const isFormValid =
		cardState.number.complete &&
		cardState.expiry.complete &&
		cardState.cvc.complete;

	// Create payment method when form is valid
	const createPaymentMethod = async () => {
		if (!stripe || !elements || isProcessing) return;

		setIsProcessing(true);

		try {
			const cardNumberElement = elements.getElement(CardNumberElement);

			const { error, paymentMethod } = await stripe.createPaymentMethod({
				type: "card",
				card: cardNumberElement,
			});

			if (error) {
				console.error("Payment method error:", error);
				setValue("card_valid", false);
				return;
			}

			// Set form values
			setValue("payment_method_id", paymentMethod.id);
			setValue("card_details", {
				brand: paymentMethod.card.brand,
				last4: paymentMethod.card.last4,
				exp_month: paymentMethod.card.exp_month,
				exp_year: paymentMethod.card.exp_year,
			});
			setValue(`${id}_card_valid`, true);
		} catch (err) {
			console.error("Error creating payment method:", err);
			setValue(`${id}_card_valid`, false);
		} finally {
			setIsProcessing(false);
		}
	};

	// Create payment method when form becomes valid, or when card number changes (after clearing)
	useEffect(() => {
		if (isFormValid && !isProcessing) {
			createPaymentMethod();
		} else {
			setValue(`${id}_card_valid`, false);
		}
	}, [isFormValid, cardState.number.complete]);

	return (
		<div className="credit-debit-payment">
			<h2>Card details</h2>
			<div className="payment-form">
				<div className="form-group">
					<label className="form-label">Card Number</label>
					<div
						className={`validation-wrap ${
							cardState.number.error ? "error" : ""
						}`}
					>
						<CardNumberElement
							options={{
								...ELEMENT_OPTIONS,
								placeholder: "1234 1234 1234 1234",
							}}
							onChange={handleCardChange("number")}
						/>
					</div>
					{cardState.number.error && (
						<span className="field__error">
							{cardState.number.error.message}
						</span>
					)}
				</div>

				<div className="form-group">
					<label className="form-label">Expiry Date</label>
					<div
						className={`validation-wrap ${
							cardState.expiry.error ? "error" : ""
						}`}
					>
						<CardExpiryElement
							options={{
								...ELEMENT_OPTIONS,
								placeholder: "MM / YY",
							}}
							onChange={handleCardChange("expiry")}
						/>
					</div>
					{cardState.expiry.error && (
						<span className="field__error">
							{cardState.expiry.error.message}
						</span>
					)}
				</div>

				<div className="form-group" style={{ marginBottom: 0 }}>
					<label className="form-label">CVC</label>
					<div
						className={`validation-wrap ${cardState.cvc.error ? "error" : ""}`}
					>
						<CardCvcElement
							options={{
								...ELEMENT_OPTIONS,
								placeholder: "123",
							}}
							onChange={handleCardChange("cvc")}
						/>
					</div>
					{cardState.cvc.error && (
						<span className="field__error">{cardState.cvc.error.message}</span>
					)}
				</div>

				<div className="payment-info">
					<p className="text-muted">
						<small>
							Your payment information is secure and encrypted. We use Stripe to
							process payments.
						</small>
					</p>
				</div>

				{isProcessing && (
					<div style={{ marginTop: "10px" }}>
						<small>Validating card details...</small>
					</div>
				)}
				{errors?.[`${id}_card_valid`] && (
					<span className="field__error">
						{errors[`${id}_card_valid`].message}
					</span>
				)}
			</div>
		</div>
	);
};

// Main component wrapper
const CreditDebit = (props) => {
	// Create Stripe promise with form-specific key
	const stripePromise = props.config.stripe_public_key
		? loadStripe(props.config.stripe_public_key)
		: null;

	// If no Stripe key is available, show an error or fallback
	if (!stripePromise) {
		return (
			<div className="credit-debit-payment">
				<div className="payment-error">
					<p>Credit/Debit processing is not available at this time.</p>
				</div>
			</div>
		);
	}

	return (
		<Elements stripe={stripePromise}>
			<CreditDebitForm {...props} />
		</Elements>
	);
};

export default CreditDebit;
